// Customer orders (M7, WP7.2): custom cakes and wholesale orders by the day they are promised,
// with their status, payment and handover, and whether planning can cover them. The customer's
// name is the only personal detail an order keeps (POPIA-minimal).

import type { OrderSummary } from '@enp/domain/api';
import { ORDER_COVERAGE, ORDER_KINDS } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  formatTime,
  Input,
  PageHeader,
  StatusBadge,
  type ColumnDef,
} from '@enp/ui';
import { keepPreviousData, useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ClipboardList, Plus, Search, SearchX, X } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import {
  ORDER_VIEWS,
  ordersQuery,
  ordersSearchSchema,
  type OrdersSearch,
} from '../../../orders/queries.js';
import {
  dueText,
  FULFILMENT_STATUS,
  KIND_LABEL,
  money,
  ORDER_STATUS,
  PAYMENT_STATUS,
} from '../../../orders/words.js';
import { ORDER_COVERAGE_LABEL, orderCoverageBadge } from '../../../planning/words.js';
import { NARROW_ONLY, useSearchText, WIDE_COLUMN } from '../../../production/parts.js';
import { KeyHints, usePageKeys } from '../../../purchasing/parts.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { usePageSearch } from '../../../shell/signals.js';
import { DayRange, FilterSelect, LoadMoreFooter } from '../../../stock/components.js';
import { accountToday, plainDateLabel } from '../../../stock/labels.js';
import { clean } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/orders/')({
  validateSearch: ordersSearchSchema,
  component: OrdersPage,
});

const VIEW_LABEL: Record<(typeof ORDER_VIEWS)[number], string> = {
  open: 'Open orders',
  completed: 'Completed',
  cancelled: 'Cancelled',
  all: 'Every order',
};

function columns(timeZone: string, today: string): ColumnDef<OrderSummary, unknown>[] {
  const col = createColumnHelper<OrderSummary>();
  return [
    col.display({
      id: 'promised',
      header: 'Promised',
      cell: ({ row }) => {
        const o = row.original;
        const due = dueText(o, today);
        return (
          <span className="flex flex-col whitespace-nowrap leading-tight">
            <span className="text-fg">
              {o.promised_day ? plainDateLabel(o.promised_day, today) : 'No time'}
              {o.promised_at && (
                <span className="tabular-nums text-muted">
                  {' '}
                  {formatTime(o.promised_at, { timeZone })}
                </span>
              )}
            </span>
            {due && (
              <span
                className={cn('text-xs', due.late ? 'font-medium text-state-danger' : 'text-muted')}
              >
                {due.text}
              </span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'order',
      header: 'Order',
      meta: { width: 'min-w-48' },
      cell: ({ row }) => {
        const o = row.original;
        return (
          <span className="flex flex-col leading-tight">
            <Link
              to="/orders/$orderId"
              params={{ orderId: o.id }}
              className="font-medium text-fg hover:underline"
            >
              {o.customer_name ?? 'No name'}
            </Link>
            <span className="text-xs text-muted">
              <span className="font-mono">{o.number}</span> · {KIND_LABEL[o.kind]}
              <span className={NARROW_ONLY}>
                {' '}
                · {o.line_count} {o.line_count === 1 ? 'line' : 'lines'}
              </span>
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'total',
      header: 'Total',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const o = row.original;
        const dead = o.order_status === 'cancelled';
        return (
          <span className="flex flex-col items-end leading-tight">
            <span
              className={cn(
                'whitespace-nowrap tabular-nums',
                dead ? 'text-muted line-through' : 'font-medium text-fg',
              )}
            >
              {money(o.total_cents)}
            </span>
            {dead && <span className="text-xs text-muted">Not owed</span>}
          </span>
        );
      },
    }),
    col.display({
      id: 'lines',
      header: 'Lines',
      meta: { align: 'right', className: WIDE_COLUMN },
      cell: ({ row }) => <span className="tabular-nums">{row.original.line_count}</span>,
    }),
    col.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const o = row.original;
        const s = ORDER_STATUS[o.order_status];
        return (
          <span className="flex flex-col items-start gap-1 leading-tight">
            <StatusBadge status={s.status}>{s.label}</StatusBadge>
            {o.order_status !== 'cancelled' && (
              <span className="whitespace-nowrap text-xs text-muted">
                {PAYMENT_STATUS[o.payment_status].label} ·{' '}
                {FULFILMENT_STATUS[o.fulfilment_status].label}
              </span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'coverage',
      header: 'Coverage',
      cell: ({ row }) => {
        const o = row.original;
        const c = orderCoverageBadge(o);
        const closed = o.order_status === 'completed' || o.order_status === 'cancelled';
        return (
          <StatusBadge
            status={c.status}
            title={c.hint}
            data-coverage={closed ? 'closed' : o.coverage}
          >
            {c.label}
          </StatusBadge>
        );
      },
    }),
    col.display({
      id: 'branch',
      header: 'Branch',
      meta: { className: WIDE_COLUMN },
      cell: ({ row }) => <span className="text-muted">{row.original.branch?.name ?? '—'}</span>,
    }),
  ];
}

function OrdersPage() {
  useDocumentTitle('Orders');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const timeZone = me.account.timezone;
  const today = accountToday(me.account);
  const canEdit = me.permissions.includes('orders.edit');
  const setSearch = (patch: Partial<OrdersSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  const [text, setText] = useSearchText(search.q, (q) => setSearch({ q }));
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);
  usePageKeys({ n: canEdit ? () => void navigate({ to: '/orders/new' }) : undefined });

  const list = useInfiniteQuery({ ...ordersQuery(search), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total;
  const filtered = Boolean(search.q || search.kind || search.coverage || search.from || search.to);
  const view = search.view ?? 'open';
  const clear = () =>
    setSearch({
      q: undefined,
      kind: undefined,
      coverage: undefined,
      from: undefined,
      to: undefined,
    });

  // Counted only when every row is loaded, so a figure never covers part of the list.
  const counts = useMemo(() => {
    if (list.hasNextPage) return null;
    const c = { uncovered: 0, partly: 0, at_risk: 0, late: 0, unchecked: 0 };
    for (const o of items) {
      // Finished and cancelled orders are closed: never "not checked" (UI7A-108).
      if (o.order_status === 'completed' || o.order_status === 'cancelled') continue;
      // Partly covered is counted as such, never as uncovered (D-074, UI7A-103).
      if (o.coverage === 'uncovered') {
        if (o.short_lines.some((l) => Number(l.covered_qty) > 0)) c.partly += 1;
        else c.uncovered += 1;
      }
      if (o.coverage === 'at_risk') c.at_risk += 1;
      if (o.coverage === 'not_checked' || o.coverage === 'not_evaluated') c.unchecked += 1;
      if (dueText(o, today)?.late) c.late += 1;
    }
    return c;
  }, [items, list.hasNextPage, today]);

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No orders match"
      description="No order fits these filters."
      action={
        <Button variant="secondary" icon={<X aria-hidden />} onClick={clear}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={ClipboardList}
      title={view === 'open' ? 'No open orders' : `No ${view === 'all' ? '' : `${view} `}orders`}
      description="Custom cakes and wholesale orders you take here get a promised time; planning checks each one against what’s planned and in stock."
      action={
        canEdit ? (
          <Button asChild>
            <Link to="/orders/new">
              <Plus aria-hidden />
              Take an order
            </Link>
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Orders"
        description="Custom and wholesale orders by the day they’re promised, and whether they’re covered."
        actions={
          canEdit ? (
            <Button asChild>
              <Link to="/orders/new" aria-keyshortcuts="n">
                <Plus aria-hidden />
                New order
              </Link>
            </Button>
          ) : undefined
        }
      />
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Order filters"
      >
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <label htmlFor="order-search" className="sr-only">
            Search orders
          </label>
          <Input
            id="order-search"
            ref={searchRef}
            type="search"
            value={text}
            placeholder="Customer or order number"
            aria-keyshortcuts="/"
            onChange={(e) => setText(e.target.value)}
            className="w-64 pl-8"
          />
        </div>
        <FilterSelect
          label="Status"
          value={search.view}
          allLabel={VIEW_LABEL.open}
          options={ORDER_VIEWS.filter((v) => v !== 'open').map((v) => ({
            value: v,
            label: VIEW_LABEL[v],
          }))}
          onChange={(v) => setSearch({ view: v as OrdersSearch['view'] })}
        />
        <FilterSelect
          label="Kind"
          value={search.kind}
          allLabel="Every kind"
          options={ORDER_KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
          onChange={(v) => setSearch({ kind: v as OrdersSearch['kind'] })}
        />
        <FilterSelect
          label="Coverage"
          value={search.coverage}
          allLabel="Any coverage"
          options={ORDER_COVERAGE.map((c) => ({ value: c, label: ORDER_COVERAGE_LABEL[c].label }))}
          onChange={(v) => setSearch({ coverage: v as OrdersSearch['coverage'] })}
        />
        <DayRange
          idPrefix="promised"
          from={search.from}
          to={search.to}
          today={today}
          onChange={(r) => setSearch({ from: r.from, to: r.to })}
        />
        {filtered && (
          <Button variant="ghost" icon={<X aria-hidden />} onClick={clear}>
            Clear
          </Button>
        )}
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        {total !== undefined && items.length > 0 ? (
          <p className="text-sm text-muted" aria-live="polite" data-testid="orders-summary">
            {formatCount(total)} {total === 1 ? 'order' : 'orders'}
            {counts && counts.uncovered > 0 && (
              <span className="font-medium text-state-danger"> · {counts.uncovered} uncovered</span>
            )}
            {counts && counts.partly > 0 && (
              <span className="font-medium text-state-danger">
                {' '}
                · {counts.partly} partly covered
              </span>
            )}
            {counts && counts.at_risk > 0 && (
              <span className="text-fg"> · {counts.at_risk} at risk</span>
            )}
            {counts && counts.late > 0 && (
              <span className="font-medium text-state-danger"> · {counts.late} late</span>
            )}
            {/* Never hidden (UI7A-6): an order nobody checked is not a covered one. */}
            {counts && counts.unchecked > 0 && (
              <span className="text-fg"> · {counts.unchecked} not checked</span>
            )}
          </p>
        ) : (
          <span />
        )}
        <KeyHints
          keys={[...(canEdit ? ([['n', 'new order']] as [string, string][]) : []), ['/', 'search']]}
        />
      </div>

      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The orders"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label="Orders"
          columns={columns(timeZone, today)}
          data={items}
          loading={list.isPending}
          empty={empty}
          filterable={false}
          getRowId={(o) => o.id}
          onRowActivate={(o) =>
            void navigate({ to: '/orders/$orderId', params: { orderId: o.id } })
          }
          footer={
            !list.isPending && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                total={total}
                noun={['order', 'orders']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
        />
      )}
    </>
  );
}
